import type { DevelopmentActionStatus, DevelopmentPlanStatus } from '@/types/developmentPlan'

export const PLAN_LIMITS = { title: 120, actionTitle: 200, description: 2000, note: 2000, actions: 50 } as const

export const ACTION_STATUSES: DevelopmentActionStatus[] = ['TODO', 'IN_PROGRESS', 'DONE']

export const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number }; status?: number })?.response?.status ?? (err as { status?: number })?.status

export const messageOf = (err: unknown): string | undefined =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message

/** Pill colors of a plan's status. */
export const PLAN_STATUS_STYLE: Record<DevelopmentPlanStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  PENDING_APPROVAL: 'bg-amber-50 text-amber-700',
  APPROVED: 'bg-emerald-50 text-emerald-700',
  COMPLETED: 'bg-blue-50 text-blue-700'
}

export const ACTION_STATUS_STYLE: Record<DevelopmentActionStatus, string> = {
  TODO: 'bg-gray-100 text-gray-700',
  IN_PROGRESS: 'bg-blue-50 text-blue-700',
  DONE: 'bg-emerald-50 text-emerald-700'
}

/**
 * A `YYYY-MM-DD` day as the reader says it ("15 de dez. de 2026"). The day is
 * already the community's (the API's), so it is read in UTC and never shifted
 * through the browser's time zone.
 */
export const formatDay = (day: string | null | undefined, locale: string): string => {
  if (!day) return ''
  const [y, m, d] = day.split('-').map(Number)
  if (!y || !m || !d) return day
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)))
}

/** Today as `YYYY-MM-DD` in a time zone (the community's), for a new plan's default period. */
export const dayIn = (timeZone: string | undefined, date = new Date()): string => {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: timeZone || undefined, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date)
  } catch {
    return date.toISOString().slice(0, 10)
  }
}

/** `day` plus whole months, clamped to the month's last day. */
export const addMonths = (day: string, months: number): string => {
  const [y, m, d] = day.split('-').map(Number)
  const target = new Date(Date.UTC(y, m - 1 + months, 1))
  const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate()
  target.setUTCDate(Math.min(d, last))
  return target.toISOString().slice(0, 10)
}

export const isDay = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value)
